import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { OperationsHealthCard } from "../components/OperationsHealthCard";
import { StatusBadge } from "../components/StatusBadge";
import { FinanceTrendChart } from "../components/FinanceTrendChart";
import { brl, fmtTime } from "../lib/format";
import { useI18n } from "../i18n/I18nContext";
import {
  Activity,
  CalendarDays,
  CheckCircle2,
  CircleDollarSign,
  Users2,
  MapPin,
  PackageOpen,
  Trophy,
  Home,
} from "lucide-react";
import { Link } from "react-router-dom";
import { MultiMarkerMap, renderPopupToHTML } from "../components/MultiMarkerMap";

const FREQ_COLORS = {
  Semanal: "#2563EB",
  Quinzenal: "#10B981",
  Mensal: "#F59E0B",
  Eventual: "#7C3AED",
  default: "#475569",
};

function Stat({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="cm-card p-3 sm:p-5 min-w-0" data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] sm:text-xs uppercase tracking-widest text-slate-500 font-semibold leading-tight">
            {label}
          </div>
          <div className="mt-1.5 sm:mt-2 text-xl sm:text-3xl font-bold text-slate-900 font-[Outfit] truncate">
            {value}
          </div>
        </div>
        <div
          className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${accent}1A`, color: accent }}
        >
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}

function DaySummaryCard({ data }) {
  const { t } = useI18n();
  if (!data || !data.teams || data.teams.length === 0) return null;
  const { teams, totals } = data;
  return (
    <div className="cm-card p-4 sm:p-5 mb-6" data-testid="day-summary-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <CalendarDays size={18} className="text-blue-600" /> {t("Progresso do dia por equipe")}
        </h2>
        <div className="flex items-center gap-3 text-xs font-semibold">
          <span className="text-emerald-600">{totals.completed} {t("feitas")}</span>
          <span className="text-blue-600">{totals.in_progress} {t("em campo")}</span>
          <span className="text-slate-500">{totals.pending} {t("pendentes")}</span>
        </div>
      </div>
      <div className="space-y-3">
        {teams.map((tm) => {
          const done = tm.completed;
          const pct = tm.total > 0 ? Math.round((done / tm.total) * 100) : 0;
          return (
            <Link
              key={tm.team_id}
              to={`/schedule?view=day&team=${tm.team_id}`}
              data-testid={`day-summary-team-${tm.team_id}`}
              className="block rounded-lg -mx-1 px-1 py-1 hover:bg-slate-50 transition-colors group"
            >
              <div className="flex items-center justify-between mb-1 text-sm">
                <span className="flex items-center gap-2 font-medium text-slate-800 min-w-0">
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ background: tm.team_color }}
                  />
                  <span className="truncate group-hover:underline">{tm.team_name}</span>
                </span>
                <span className="text-xs text-slate-500 flex-shrink-0 ml-2">
                  {done}/{tm.total}
                  {tm.in_progress > 0 && (
                    <span className="text-blue-600"> · {tm.in_progress} {t("em campo")}</span>
                  )}
                </span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function ProductivityCard({ items }) {
  const { t } = useI18n();
  if (!items || items.length === 0) return null;
  const monthLabel = new Date().toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
  const max = Math.max(...items.map((i) => i.jobs_finished), 1);
  const medals = ["#F59E0B", "#94A3B8", "#B45309"];
  return (
    <div className="cm-card p-4 sm:p-5 mb-6" data-testid="productivity-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <Trophy size={18} className="text-amber-500" />
          {t("Ranking de produtividade")}
          <span className="text-xs font-medium text-slate-400 normal-case">
            ({monthLabel})
          </span>
        </h2>
        <Link to="/audit-log" className="text-sm text-blue-600 hover:underline">
          {t("Ver auditoria →")}
        </Link>
      </div>
      <div className="space-y-2.5">
        {items.map((it, idx) => {
          const pct = Math.round((it.jobs_finished / max) * 100);
          const hours = Math.floor((it.total_minutes || 0) / 60);
          const mins = (it.total_minutes || 0) % 60;
          return (
            <div
              key={it.user_id}
              data-testid={`productivity-row-${it.user_id}`}
              className="flex items-center gap-3"
            >
              <span
                className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0"
                style={{ background: medals[idx] || "#CBD5E1" }}
              >
                {idx + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-sm font-semibold text-slate-800 truncate">
                    {it.name}
                  </span>
                  <span className="text-xs text-slate-500 flex-shrink-0 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 font-bold text-emerald-700">
                      <Home size={12} /> {it.jobs_finished}
                    </span>
                    {(it.total_minutes || 0) > 0 && (
                      <span className="text-slate-400">
                        {hours > 0 ? `${hours}h ` : ""}{mins}min
                      </span>
                    )}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-amber-400 transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [todayJobs, setTodayJobs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [clients, setClients] = useState([]);
  const [receivables, setReceivables] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [todaySummary, setTodaySummary] = useState(null);
  const [productivity, setProductivity] = useState([]);

  async function load() {
    const today = new Date().toISOString().slice(0, 10);
    const [d, j, t, c, rcv, ls, exp, ds, pr] = await Promise.all([
      api.get("/dashboard"),
      api.get(`/jobs?from=${today}T00:00:00&to=${today}T23:59:59`),
      api.get("/teams"),
      api.get("/clients"),
      api.get("/receivables/summary").catch(() => ({ data: null })),
      api.get("/supplies/restock-suggestions").catch(() => ({ data: [] })),
      api.get("/clients/expiring?days=30").catch(() => ({ data: [] })),
      api.get(`/jobs/today-summary?date=${today}`).catch(() => ({ data: null })),
      api.get("/jobs/productivity-ranking").catch(() => ({ data: { ranking: [] } })),
    ]);
    setStats(d.data);
    setTodayJobs(j.data);
    setTeams(t.data);
    setClients(c.data);
    setReceivables(rcv.data);
    setLowStock(ls.data || []);
    setExpiring(exp.data || []);
    setTodaySummary(ds.data);
    setProductivity((pr.data && pr.data.ranking) || []);
  }

  useEffect(() => {
    load();
    const id = setInterval(load, 60000); // atualiza o resumo do dia ~1min
    return () => clearInterval(id);
  }, []);

  const clientsWithGps = useMemo(
    () => clients.filter((c) => c.lat && c.lng),
    [clients],
  );

  const noRoutineCount = useMemo(
    () => clients.filter((c) => !(c.schedule_config && c.schedule_config.team_id)).length,
    [clients],
  );

  const mapMarkers = useMemo(
    () =>
      clientsWithGps.map((c) => ({
        id: c.id,
        lat: c.lat,
        lng: c.lng,
        color: FREQ_COLORS[c.frequency] || FREQ_COLORS.default,
        popupHTML: renderPopupToHTML(
          <div style={{ fontSize: 13, minWidth: 200 }}>
            <div style={{ fontWeight: 600, color: "#0F172A" }}>{c.name}</div>
            <div style={{ fontSize: 11, color: "#64748B", marginBottom: 4 }}>
              {c.address}
              {c.apt_number ? ` · Apt ${c.apt_number}` : ""}
            </div>
            <div style={{ fontSize: 11, color: "#64748B" }}>
              {[c.city, c.state, c.zip_code].filter(Boolean).join(", ")}
            </div>
            {c.frequency && (
              <div style={{ marginTop: 6 }}>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    padding: "2px 8px",
                    borderRadius: 9999,
                    color: "#fff",
                    background: FREQ_COLORS[c.frequency] || FREQ_COLORS.default,
                  }}
                >
                  {c.frequency}
                </span>
              </div>
            )}
          </div>,
        ),
      })),
    [clientsWithGps],
  );

  const finishedTeams = teams.filter((t) => t.day_complete);
  const workingTeams = teams.filter(
    (t) => (t.today_total || 0) > 0 && !t.day_complete,
  );

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-end justify-between gap-2 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {t("Hoje")}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold">{t("Painel operacional")}</h1>
        </div>
        <div className="text-sm text-slate-500">
          {new Date().toLocaleDateString("en-US", {
            weekday: "long",
            day: "2-digit",
            month: "long",
            year: "numeric",
          })}
        </div>
      </div>

      <OperationsHealthCard />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4 mb-6">
        <Stat
          icon={CalendarDays}
          label={t("Limpezas hoje")}
          value={stats?.today_total ?? "—"}
          accent="#2563EB"
          testId="stat-today-total"
        />
        <Stat
          icon={Activity}
          label={t("Em andamento")}
          value={stats?.running ?? "—"}
          accent="#3B82F6"
          testId="stat-running"
        />
        <Stat
          icon={CheckCircle2}
          label={t("Finalizadas")}
          value={stats?.today_finished ?? "—"}
          accent="#10B981"
          testId="stat-finished"
        />
        <Stat
          icon={Users2}
          label={t("Equipes ativas")}
          value={stats?.teams_active ?? "—"}
          accent="#F59E0B"
          testId="stat-teams"
        />
        <Stat
          icon={CircleDollarSign}
          label={t("Faturamento hoje")}
          value={brl(stats?.revenue_today ?? 0)}
          accent="#0F172A"
          testId="stat-revenue"
        />
      </div>

      <DaySummaryCard data={todaySummary} />

      <ProductivityCard items={productivity} />

      <div className="mb-6">
        <FinanceTrendChart months={6} />
      </div>

      <div className="cm-card p-5" data-testid="dashboard-map">
        {receivables && receivables.em_atraso > 0 && (
          <Link
            to="/recebimentos"
            data-testid="dashboard-overdue-alert"
            className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 hover:bg-rose-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center">
                <span className="text-lg">⚠</span>
              </div>
              <div>
                <div className="font-semibold text-rose-900 text-sm">
                  {receivables.em_atraso}{" "}
                  {receivables.em_atraso > 1 ? t("clientes em atraso") : t("cliente em atraso")}
                </div>
                <div className="text-xs text-rose-700/80">
                  Total: ${Number(receivables.valor_em_atraso || 0).toFixed(2)} {t("a receber")}
                </div>
              </div>
            </div>
            <span className="text-xs text-rose-700 font-semibold">{t("Ver detalhes →")}</span>
          </Link>
        )}
        {lowStock.length > 0 && (
          <Link
            to="/supplies"
            data-testid="dashboard-low-stock-alert"
            className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 hover:bg-amber-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                <PackageOpen size={18} />
              </div>
              <div>
                <div className="font-semibold text-amber-900 text-sm">
                  {lowStock.length}{" "}
                  {lowStock.length > 1
                    ? t("suprimentos no/abaixo do estoque mínimo")
                    : t("suprimento no/abaixo do estoque mínimo")}
                </div>
                <div className="text-xs text-amber-700/80 truncate max-w-md">
                  {lowStock.slice(0, 3).map((s) => s.name).join(" · ")}
                  {lowStock.length > 3 ? ` · +${lowStock.length - 3}` : ""}
                </div>
              </div>
            </div>
            <span className="text-xs text-amber-700 font-semibold">{t("Repor →")}</span>
          </Link>
        )}
        {expiring.length > 0 && (
          <ExpiringContractsWidget items={expiring} />
        )}
        {clients.length > 0 && noRoutineCount > 0 && (
          <Link
            to="/clients?routine=without"
            data-testid="dashboard-no-routine-widget"
            className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors px-4 py-3 mb-3"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                <CalendarDays size={18} />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-amber-900">
                  {t("{count} de {total} clientes sem rotina recorrente", {
                    count: noRoutineCount,
                    total: clients.length,
                  })}
                </div>
                <div className="text-xs text-amber-700">
                  {t("Configure agendas recorrentes e transforme em receita previsível.")}
                </div>
              </div>
            </div>
            <span className="text-xs text-amber-700 font-semibold whitespace-nowrap">{t("Configurar →")}</span>
          </Link>
        )}
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <MapPin size={20} className="text-blue-600" /> {t("Mapa das casas registradas")}
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-semibold">
              {clientsWithGps.length} {t("clientes")}
            </span>
          </h2>
          <Link to="/clients/map" className="text-sm text-blue-600 hover:underline">
            {t("Abrir mapa completo →")}
          </Link>
        </div>
        {clientsWithGps.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">
            {t("Nenhum cliente com GPS capturado ainda.")}
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <MultiMarkerMap markers={mapMarkers} height="380px" />
          </div>
        )}
      </div>

      <div className="cm-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold flex items-center gap-2">
            🏁 {t("Equipes finalizadas hoje")}
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
              {finishedTeams.length}/{teams.length}
            </span>
          </h2>
          <Link
            to="/teams"
            className="text-sm text-blue-600 hover:underline"
          >
            {t("Ver todas →")}
          </Link>
        </div>
        {teams.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">
            {t("Nenhuma equipe cadastrada.")}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {finishedTeams.length === 0 && workingTeams.length === 0 && (
              <div className="col-span-full text-sm text-slate-500 py-4 text-center">
                {t("Nenhuma equipe com casas atribuídas para hoje.")}
              </div>
            )}
            {finishedTeams.map((tm) => (
              <div
                key={tm.id}
                data-testid={`dashboard-team-done-${tm.id}`}
                className="p-4 rounded-xl border-2 border-emerald-200 bg-emerald-50 relative overflow-hidden"
              >
                <div
                  className="absolute top-0 left-0 right-0 h-1"
                  style={{ background: tm.color || "#10B981" }}
                />
                <div className="flex items-start justify-between mb-1">
                  <div className="font-semibold text-slate-900 flex items-center gap-2">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full"
                      style={{ background: tm.color || "#10B981" }}
                    />
                    {tm.name}
                  </div>
                  <span className="text-emerald-700 font-bold text-xs">
                    {t("✓ Concluído")}
                  </span>
                </div>
                <div className="text-xs text-slate-600">
                  {tm.today_finished}/{tm.today_total} {t("casas finalizadas")}
                </div>
              </div>
            ))}
            {workingTeams.map((tm) => (
              <div
                key={tm.id}
                data-testid={`dashboard-team-working-${tm.id}`}
                className="p-4 rounded-xl border border-slate-200 bg-white relative overflow-hidden"
              >
                <div
                  className="absolute top-0 left-0 right-0 h-1"
                  style={{ background: tm.color || "#2563EB" }}
                />
                <div className="flex items-start justify-between mb-1">
                  <div className="font-semibold text-slate-900 flex items-center gap-2">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full"
                      style={{ background: tm.color || "#2563EB" }}
                    />
                    {tm.name}
                  </div>
                  <span className="text-blue-700 font-semibold text-xs animate-pulse">
                    {t("Em campo")}
                  </span>
                </div>
                <div className="text-xs text-slate-600 mb-2">
                  {tm.today_finished}/{tm.today_total} {t("casas")}
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 transition-all"
                    style={{
                      width: `${
                        tm.today_total
                          ? Math.round(
                              (tm.today_finished / tm.today_total) * 100,
                            )
                          : 0
                      }%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="cm-card p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Limpezas de hoje</h2>
          <Link
            to="/schedule"
            data-testid="dashboard-view-schedule"
            className="text-sm text-blue-600 hover:underline"
          >
            Ver agenda completa →
          </Link>
        </div>
        {todayJobs.length === 0 ? (
          <div className="text-sm text-slate-500 py-10 text-center">
            Nenhuma limpeza agendada para hoje.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3 font-medium">{t("Horário")}</th>
                  <th className="py-2 pr-3 font-medium">{t("Cliente")}</th>
                  <th className="py-2 pr-3 font-medium">{t("Equipe")}</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 pr-3 font-medium text-right">{t("Ação")}</th>
                </tr>
              </thead>
              <tbody>
                {todayJobs.map((j) => (
                  <JobRow key={j.id} job={j} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function JobRow({ job }) {
  const { t } = useI18n();
  const [client, setClient] = useState(null);
  const [team, setTeam] = useState(null);
  useEffect(() => {
    api.get(`/clients/${job.client_id}`).then((r) => setClient(r.data)).catch(() => {});
    api.get("/teams").then((r) => setTeam(r.data.find((t) => t.id === job.team_id)));
  }, [job]);

  return (
    <tr
      className={`border-b border-slate-100 hover:bg-slate-50/60 ${
        job.status === "andamento" ? "job-live" : ""
      }`}
    >
      <td className="py-2.5 pr-3 font-medium text-slate-800">
        {fmtTime(job.scheduled_at)}
      </td>
      <td className="py-2.5 pr-3">
        <div className="font-medium text-slate-900">{client?.name || "…"}</div>
        <div className="text-xs text-slate-500">{client?.address}</div>
      </td>
      <td className="py-2.5 pr-3">{team?.name || "—"}</td>
      <td className="py-2.5 pr-3">
        <StatusBadge status={job.status} job={job} />
      </td>
      <td className="py-2.5 pr-3 text-right">
        <Link
          to={`/jobs/${job.id}`}
          data-testid={`job-link-${job.id}`}
          className="text-blue-600 hover:underline text-sm"
        >
          {t("Abrir")}
        </Link>
      </td>
    </tr>
  );
}


function ExpiringContractsWidget({ items }) {
  const { t } = useI18n();
  const overdue = items.filter((x) => x.days_remaining < 0);
  const expiringSoon = items.filter((x) => x.days_remaining >= 0);
  const totalValue = items.reduce((s, x) => s + (Number(x.value) || 0), 0);
  const tone =
    overdue.length > 0
      ? "border-rose-200 bg-rose-50 hover:bg-rose-100"
      : "border-orange-200 bg-orange-50 hover:bg-orange-100";
  const textTone = overdue.length > 0 ? "text-rose-900" : "text-orange-900";
  const subTone = overdue.length > 0 ? "text-rose-700/80" : "text-orange-700/80";
  const iconTone = overdue.length > 0 ? "bg-rose-100 text-rose-700" : "bg-orange-100 text-orange-700";

  return (
    <div
      data-testid="dashboard-expiring-contracts"
      className={`mb-4 rounded-xl border ${tone} transition-colors`}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg ${iconTone} flex items-center justify-center`}>
            <CalendarDays size={18} />
          </div>
          <div>
            <div className={`font-semibold text-sm ${textTone}`}>
              {items.length} {items.length > 1 ? t("contratos") : t("contrato")}{" "}
              {overdue.length > 0 ? `${t("vencido(s) ou")} ` : ""}
              {t("expirando em 30 dias")}
            </div>
            <div className={`text-xs ${subTone}`}>
              {t("Receita recorrente em risco:")} {brl(totalValue)}{t("/limpeza")}
            </div>
          </div>
        </div>
      </div>
      <div className="px-4 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {items.slice(0, 6).map((c) => (
          <Link
            key={c.id}
            to={`/clients?edit=${c.id}`}
            data-testid={`expiring-client-${c.id}`}
            className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white/70 hover:bg-white border border-white/60 hover:border-slate-200 transition-colors"
          >
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">
                {c.name}
              </div>
              <div className="text-[11px] text-slate-500">
                {c.frequency || "—"} · {brl(c.value || 0)}
              </div>
            </div>
            <div
              className={`text-[11px] font-semibold whitespace-nowrap px-2 py-0.5 rounded-full border ${
                c.days_remaining < 0
                  ? "bg-rose-100 text-rose-800 border-rose-200"
                  : c.days_remaining <= 7
                    ? "bg-orange-100 text-orange-800 border-orange-200"
                    : "bg-amber-100 text-amber-800 border-amber-200"
              }`}
            >
              {c.days_remaining < 0
                ? t("Vencido há {n}d", { n: Math.abs(c.days_remaining) })
                : c.days_remaining === 0
                  ? t("Vence hoje")
                  : t("{n}d restantes", { n: c.days_remaining })}
            </div>
          </Link>
        ))}
        {items.length > 6 && (
          <Link
            to="/clients"
            className="col-span-full text-center text-xs font-semibold text-slate-700 hover:text-slate-900 py-1"
          >
            {t("Ver todos ({n}) →", { n: items.length })}
          </Link>
        )}
      </div>
    </div>
  );
}
